<script setup lang="ts">
import {useUserAvatar} from "@/composables/avatar";
import {router, usePage} from "@inertiajs/vue3";
import {computed, Ref, ref} from "vue";
import {DataTableHeader, ExtendedProject, User} from "@/types";
import {route} from "ziggy-js";
import TextFilterBtn from "@/Pages/projects/users/components/TextFilterBtn.vue";
import TextField from "@/components/forms/TextField.vue";
import CancelInvitationDialog from "@/Pages/projects/users/components/CancelInvitationDialog.vue";
import ViewDTA from "@/Pages/projects/users/components/ViewDTA.vue";

interface Props {
    project: ExtendedProject,
    items: Array<any>,
    loading: boolean,
}
const props = defineProps<Props>()
const page = usePage()

const headers: Array<DataTableHeader> = [
    {title: 'Icon', value: 'email', width: '5%'},
    {title: 'Name', value: 'name', width: '30%'},
    {title: 'Status', value: 'full_name', width: '25%'},
    {title: 'Actions', value: 'actions', width: '40%', align: 'end'},
]

const currentUser = computed(() => page.props.user as User)

const search = ref('')
const type: Ref<'all' | 'collaborators' | 'pending-invitations'>  = ref('all')
const roles: Array<{text: string; value: string}> = [
    {
        text: 'All',
        value: 'all'
    },
    {
        text: 'Collaborators',
        value: 'collaborators'
    },
    {
        text: 'Pending invitations',
        value: 'pending-invitations'
    }
]

const update = (user: User, role: string) => {
    router.put(
        route(
            'projects.users.update',
            {
                project: props.project.slug, user: user.name
            }
        ),
        {
            role: role
        },
        {
            preserveScroll: true,
        })
}

const filteredAccesses = computed(() => {
    if (type.value === 'collaborators') {
        return props.items.filter((item) => item.validated_at)
    }

    if (type.value === 'pending-invitations') {
        return props.items.filter((item) => !item.validated_at)
    }

    return props.items
})
</script>

<template>
    <v-card
        variant="flat"
        class="border-sm"
        rounded="lg"
    >
        <v-card-title class="bg-grey-lighten-4 text-subtitle-2 text-grey-darken-2">
            Accesses
            <text-filter-btn
                v-model="type"
                title="Type"
                btn-class="float-end"
                :items="roles"
                label="Filter by member type"
            />
        </v-card-title>
        <v-card-text>
            <v-row>
                <v-col cols="12">
                    <text-field
                        v-model="search"
                        placeholder="Find a collaborator or group"
                        prepend-icon="mdi-magnify"
                        :hide-details="true"
                    />
                </v-col>
            </v-row>
        </v-card-text>
        <v-divider />
        <v-card-text class="pa-0">
            <v-data-table
                v-model:search="search"
                :headers="headers"
                :items="filteredAccesses"
                :loading="props.loading"
                no-data-text="No Results"
            >
                <template #[`item.email`]="{item}">
                    <v-avatar
                        v-if="!item.validated_at"
                        size="36"
                    >
                        <v-icon size="36">
                            mdi-email-outline
                        </v-icon>
                    </v-avatar>
                    <v-avatar
                        v-if="item.validated_at"
                        size="36"
                        class="border-sm"
                        rounded="xl"
                    >
                        <v-img
                            :src="useUserAvatar(item.name)"
                            alt="avatar"
                        />
                    </v-avatar>
                </template>
                <template #[`item.name`]="{item}">
                    <a
                        class="font-weight-bold text-body-2 hover-underline cursor-pointer"
                        @click="router.visit(route('users.show', item.name))"
                    >
                        {{ item.name }}
                    </a>
                    <v-chip
                        v-if="item.role === 'dta'"
                        class="ml-1"
                        color="success"
                        size="x-small"
                    >
                        Authorized
                    </v-chip>
                    <br>
                    <div class="text-grey-darken-2 text-caption">
                        {{ item.validated_at ? item.email : 'Awaiting response' }}
                    </div>
                </template>
                <template #[`item.full_name`]="{item}">
                    <div
                        v-if="!item.validated_at"
                        class="text-grey-darken-2 text-caption"
                    >
                        Pending Invite
                    </div>
                </template>
                <template #[`item.actions`]="{item}">
                    <view-d-t-a
                        v-if="item.role === 'dta'"
                        :dta="item.dta"
                    />
                    <v-menu
                        v-if="item.name !== currentUser.name && item.role !== 'dta'"
                        max-width="350"
                    >
                        <template #activator="{ props: attrs }">
                            <v-btn
                                v-bind="attrs"
                                size="small"
                                variant="flat"
                                rounded="lg"
                                class="border text-none text-caption btn-outlined mr-5 bg-grey-lighten-4 rounded"
                            >
                                <span class="font-weight font-weight-medium text-grey-darken-3">Role: </span><span
                                    class="font-weight-medium text-capitalize"
                                >{{ item.role }}</span>
                                <v-icon
                                    right
                                    class="ml-0"
                                >
                                    mdi-menu-down
                                </v-icon>
                            </v-btn>
                        </template>
                        <v-list
                            density="compact"
                            class="py-0 border"
                            rounded="lg"
                        >
                            <v-list-item
                                class="font-weight-medium text-caption text-black"
                                density="compact"
                            >
                                Choose role
                            </v-list-item>
                            <v-divider />
                            <v-list-item
                                density="comfortable"
                                :link="true"
                                @click="update(item, 'member')"
                            >
                                <template #prepend>
                                    <v-icon
                                        color="black"
                                        size="small"
                                        class="mb-5"
                                    >
                                        {{
                                            item.role === 'member' ? 'mdi-check' : null
                                        }}
                                    </v-icon>
                                </template>
                                <v-list-item-title class="text-black font-weight-medium text-body-2">
                                    Member
                                </v-list-item-title>
                                <div class="font-weight-regular text-black text-caption">
                                    Can view own transfers
                                </div>
                            </v-list-item>
                            <v-divider />
                            <v-list-item
                                density="comfortable"
                                :link="true"
                                @click="update(item, 'admin')"
                            >
                                <template #prepend>
                                    <v-icon
                                        color="black"
                                        size="small"
                                        class="mb-10"
                                    >
                                        {{
                                            item.role === 'admin' ? 'mdi-check' : null
                                        }}
                                    </v-icon>
                                </template>
                                <v-list-item-title class="text-black font-weight-medium text-body-2">
                                    Admin
                                </v-list-item-title>
                                <div class="font-weight-regular text-black text-caption">
                                    Can view any transfer and manage project accesses
                                </div>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                    <cancel-invitation-dialog
                        v-if="item.name !== currentUser.name"
                        :user="item"
                        :project="project"
                    />
                </template>
            </v-data-table>
        </v-card-text>
    </v-card>
</template>

<style scoped>
.hover-underline:hover {
    text-decoration: underline;
}

:deep(thead) {
    display: none !important;
}

:deep(td) {
 padding-top: 15px !important;
  padding-bottom: 15px !important;
}

:deep(.v-data-table-footer) {
    display: none !important;
}

:deep(.v-list-item__prepend) {
  width: 30px;
}
</style>
